/* eslint-disable @typescript-eslint/explicit-member-accessibility */

import Feathers from "@feathersjs/feathers";
import { Autobind } from "@Internal/Utils";

import { AnnotatedJSON, Dictionary, Filter, HandleError, Identifiable } from "@Internal/Contracts";
import { CustomError, PagerSorter } from "@Internal/Models";
import { Models } from "@Internal";
import { EventBus } from "App/IOC";
import { CountDifferentFields } from "./Base";

function CompileFilters(filters: Filter[]): Dictionary {
    const result: Dictionary = {};

    filters.forEach((filter: Filter): void => {
        result[filter.Id] = filter.FormatValue().toString();
    });

    return result;
}

export async function ProcessException(exception: any): Promise<CustomError> {
    let customError: CustomError;

    /* Hack: To remove when refresh token logic is implemented. */
    if(exception.name === "NotAuthenticated")
        EventBus.Publish("InvalidAuthorization");

    if(exception.Id !== undefined)
        customError = new CustomError(exception.Id, exception.Message);

    else
        customError = new CustomError("BaseService.Get", String(exception));

    return customError;
}

export default class AdsPublicationService<T extends Identifiable> {
    private service: Feathers.Service<any>;
    private imageService: Feathers.Service<any>;
    private customerService: Feathers.Service<any>;
    private serializerService: Feathers.Service<any>;

    constructor(service: Feathers.Service<any>, 
        imageService: Feathers.Service<any>, 
        customerService: Feathers.Service<any>,
        serializerService: Feathers.Service<any>
    ) {
        this.service = service;
        this.imageService = imageService;
        this.customerService = customerService;
        this.serializerService = serializerService;
    }

    // eslint-disable-next-line max-len
    public async AddPicture(id: string, file: File): Promise<[any | undefined, CustomError | undefined]> {

        try {
            const results = await this.service.addPicture(id, file);
            return this.deserialize( results );

        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    public async DeletePicture(id: string): Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const record = await this.imageService.remove( id);

            return this.deserialize( record );
        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    public async DeleteGateWay(id: string): Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const record = await this.service.remove( id);

            return this.deserialize( record );
        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Create( record: T ): Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const results = await this.service.create( this.serialize( record, true ) );

            return this.deserialize( results );

        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Find(
        filters: Filter[],
        pagerSorter: PagerSorter
    ): Promise<[T[], PagerSorter, CustomError | undefined]> {
        const query: Dictionary = {
            ...CompileFilters(filters),
            Page: pagerSorter.Page,
            Limit: pagerSorter.Limit
        }

        if(pagerSorter.Column !== undefined) {
            query.Sort = pagerSorter.Column.Id;
            query.Descending = pagerSorter.Descending;
        }

        let results: any;

        try {
            results = await this.service.find({ query });

        } catch(error) {
            return [[], pagerSorter, await ProcessException(error)];
        }

        if(results.Error !== undefined)
            return [[], pagerSorter, new CustomError(results.Error.Id, results.Error.Message)];

        pagerSorter = pagerSorter.Copy();
        // pagerSorter.Total = results.Pagination.Total;
        if(results.Pagination && results.Pagination.Total) {
            pagerSorter.Total = results.Pagination.Total;
        } else {
            pagerSorter.Total = 0;
        }

        const ready: T[] = [];

        if(results.Items !== undefined) {
            for(const record of results.Items) {
                const [instance, error] = this.deserialize(record);
                if(error !== undefined)
                    return [[], pagerSorter, error];

                if(instance !== undefined) {
                    const result = {
                        ...instance,
                        Id: record.id
                    }
                    ready.push(result as T);
                }
            }
        }

        if(filters.length > 0 || pagerSorter.Limit === 1)
            return [ready, pagerSorter, undefined];

        return [ready, pagerSorter, undefined];
    }

    @Autobind
    async Get( id: number ): Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const record = await this.service.get( id );

            if( record === undefined )
                return [ undefined, undefined ];

            const baseUrl = process.env.CONSOLE_BASE_MEDIA_PUBLIC_URL
            || "https://s3-eu-west-3.amazonaws.com/prospeneo-public/";
            /* Only build the public URL when an image actually exists, otherwise we request
               ".../prospeneo-public/undefined" and get a 404. Empty falls back to the
               placeholder. */
            record.image = record.image ? baseUrl + record.image : undefined;

            record.AllowSold = record.allow_sold;

            return this.deserialize( record );

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Update(
        originalRecord: T,
        updatedRecord: T
    ): Promise<[ T | undefined, CustomError | undefined ]> {
        const record = this.countDifferentFields( originalRecord, updatedRecord );

        if( Object.keys( record as {} ).length === 0 )
            return [ undefined, new CustomError( -1, $t( "GLOBAL_nothingToUpdate" ) ) ];

        const payload = this.serialize( record, true );

        /* HACK: `undefined` values are unfortunately ommitted by JSON.stringify which is used
        by Featherjs REST client, so we force any `undefined` value to `null` so that
        it can be sent. */
        const replacer = ( key: string, value: any ): boolean =>
            typeof value === "undefined" ? null : value;

        let results: any;

        try {
            results = await this.service.update(
                originalRecord.Id,
                JSON.parse( JSON.stringify( payload, replacer ) )
            );

        } catch( error ) {
            console.log( error );

            return [ undefined, await ProcessException( error ) ];
        }

        return this.deserialize( results );
    }

    @Autobind
    async GetAllCustomers(
        filters: Filter[],
        pagerSorter: PagerSorter
    ): Promise<[T[], PagerSorter, CustomError | undefined]> {
        const query: Dictionary = {
            ...CompileFilters(filters),
            Page: pagerSorter.Page,
            Limit: pagerSorter.Limit
        }

        if(pagerSorter.Column !== undefined) {
            query.Sort = pagerSorter.Column.Id;
            query.Descending = pagerSorter.Descending;
        }

        let results: any;

        try {
            results = await this.customerService.find({ query });

        } catch(error) {
            return [[], pagerSorter, await ProcessException(error)];
        }

        if(results.Error !== undefined)
            return [[], pagerSorter, new CustomError(results.Error.Id, results.Error.Message)];

        pagerSorter = pagerSorter.Copy();

        /* The Customer gateway service sets SkipSerialization, so the raw upstream customer-list
           payload comes through unchanged (lowercase items/total), while other endpoints expose the
           serialized Items/Pagination shape. Accept both. */
        const items: any[] = Array.isArray(results.Items)
            ? results.Items
            : (Array.isArray(results.items) ? results.items : []);

        const total = ( results.Pagination && results.Pagination.Total )
            ? results.Pagination.Total
            : ( results.total !== undefined ? results.total : items.length );

        pagerSorter.Total = Number.isNaN(Number(total)) ? items.length : Number(total);

        const ready: T[] = [];

        /* Rows are built directly from the raw upstream fields (lowercase). No deserialize gate:
           the customer-list payload skips serialization, so Models.Deserialize would not match its
           shape and a strict failure would wrongly empty the whole list. */
        if(items.length > 0) {
            for(const record of items) {
                const company = record.company || "";
                /* Nom du client = the group owner (ROLE_OWNER). The linked customer contact
                   `name` is empty upstream on most groups, so fall back to the enriched
                   `owner` so the column, autocomplete and Name filter all have a value. */
                const owner = record.name || record.owner || "";

                /* Name = combined label for the form's Groupes multiselect; Company and Owner
                   are the split fields the attribution list shows as columns. Status drives the
                   "actif" default filter (absent upstream -> active). */
                const name = company !== "" ? `${company} - ${owner}` : owner;

                const result: any = {
                    Id: record.id,
                    Name: name,
                    Company: company,
                    Owner: owner,
                    Email: record.email || "",
                    City: record.city || "",
                    Street: record.street || "",
                    PostalCode: record.postalCode || "",
                    Status: record.status || "active",
                    Group: record.group || "",
                    /* Group-level facts joined server-side (groups + users + setup-gateway).
                       GroupOwner (ROLE_OWNER) is distinct from Owner above, which is the
                       linked customer's contact name. */
                    ParentGroup: record.parentGroup || "",
                    MemberCount:
                        record.memberCount !== undefined ? record.memberCount : "",
                    GroupOwner: record.owner || "",
                    PortalCount:
                        record.portalCount !== undefined ? record.portalCount : "",
                    PortalNames: Array.isArray( record.portalNames ) ? record.portalNames : []
                }
                ready.push(result as T);
            }
        }

        if(filters.length > 0 || pagerSorter.Limit === 1)
            return [ready, pagerSorter, undefined];

        return [ready, pagerSorter, undefined];
    }
    /* Lazy per-row enrichment for the sale-point list: member count + owner + portal count + portal
       names for a single group. GetAllCustomers returns the list without these (they cost two
       upstream calls each); the view fetches them row by row after paint. Keyed by groupId. */
    @Autobind
    async GetCustomerEnrichment(
        groupId: number
    ): Promise<[ any | undefined, CustomError | undefined ]> {
        try {
            const result = await this.customerService.get( groupId );

            if( result && result.Error !== undefined )
                return [ undefined, new CustomError( result.Error.Id, result.Error.Message ) ];

            return [ result, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async CustomerCreate( record: T ): Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const results = await this.customerService.create( this.serialize( record, true ) );

            return this.deserialize( results );

        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async DeleteCustomer( id: number, record: T ): 
    Promise<[ T | undefined, CustomError | undefined ]> {
        try {
            const results = 
            await this.customerService.update(id, this.serialize( record, true ) );

            return this.deserialize( results );

        } catch( error ) {
            console.log("error", error);
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async GetAllSerializers(
        filters: Filter[],
        pagerSorter: PagerSorter
    ): Promise<[T[], CustomError | undefined]> {
        const query: Dictionary = {
            ...CompileFilters(filters),
            Page: pagerSorter.Page,
            Limit: pagerSorter.Limit
        }

        if(pagerSorter.Column !== undefined) {
            query.Sort = pagerSorter.Column.Id;
            query.Descending = pagerSorter.Descending;
        }

        let results: any;

        try {
            results = await this.serializerService.find({ query });

        } catch(error) {
            return [[], await ProcessException(error)];
        }

        if(results.Error !== undefined)
            return [[], new CustomError(results.Error.Id, results.Error.Message)];

        pagerSorter = pagerSorter.Copy();
        // pagerSorter.Total = results.Pagination.Total;
        if(results.Pagination && results.Pagination.Total) {
            pagerSorter.Total = results.Pagination.Total;
        } else {
            pagerSorter.Total = 0;
        }

        const ready: T[] = [];

        if(results.Items !== undefined) {
            for(const record of results.Items) {
                const [instance, error] = this.deserialize(record);
                if(error !== undefined)
                    return [[], error];

                if(instance !== undefined) {
                    const result = {
                        Name: record.name,
                        Id: record.id
                    }
                    ready.push(result as any);
                }
            }
        }

        if(filters.length > 0 || pagerSorter.Limit === 1)
            return [ready, undefined];

        return [ready, undefined];
    }

    protected deserialize(result: AnnotatedJSON): HandleError<T> {
        return Models.Deserialize<T>(result);
    }

    protected serialize<T>( record: T, idOnly?: boolean ): AnnotatedJSON {
        return Models.Serialize( record, idOnly );
    }
    private countDifferentFields( originalRecord: T, updatedRecord: T ): T {
        return CountDifferentFields<T>( originalRecord, updatedRecord );
    }
}
